/** Shared display formatting for the doctor portal. */

import type { Booking } from "@/types/doctor";

export function initials(name: string): string {
  const parts = name
    .replace(/^(dr\.?|prof\.?)\s+/i, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "HN";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

export function formatCurrency(amount: number, currency = "INR"): string {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

/** "2026-08-13" — a calendar date, which carries no time and no zone. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** "14:30", "14:30:00", "2:30 pm" — a wall clock, which carries no date. */
const WALL_CLOCK = /^(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*([ap])\.?m\.?)?$/i;

interface WallClock {
  hours: number;
  minutes: number;
}

function parseWallClock(value: string): WallClock | null {
  const match = WALL_CLOCK.exec(value.trim());
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3]?.toLowerCase();
  if (meridiem === "p" && hours < 12) hours += 12;
  if (meridiem === "a" && hours === 12) hours = 0;
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
}

function toDate(value: string | number | Date): Date | null {
  // A bare "YYYY-MM-DD" is parsed as UTC midnight, which lands on the previous
  // calendar day for anyone west of Greenwich. A date with no zone is a local one.
  if (typeof value === "string" && DATE_ONLY.test(value.trim())) {
    const [year, month, day] = value.trim().split("-").map(Number);
    return new Date(year!, month! - 1, day!);
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Intl throws on an unknown IANA zone; a bad zone should not blank the field. */
function dateTimeFormat(
  options: Intl.DateTimeFormatOptions,
  timeZone?: string | null | undefined,
): Intl.DateTimeFormat {
  if (timeZone) {
    try {
      return new Intl.DateTimeFormat("en-IN", { ...options, timeZone });
    } catch {
      /* fall through to the viewer's own zone */
    }
  }
  return new Intl.DateTimeFormat("en-IN", options);
}

export function formatDate(value: string | number | Date): string {
  const date = toDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatDateTime(value: string | number | Date): string {
  const date = toDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };

/**
 * Bookings carry their time as a bare wall clock ("09:30") next to the date,
 * so this accepts both that and a full timestamp. `new Date("09:30")` is an
 * Invalid Date, which is why the range used to render as "— – —".
 */
export function formatTime(
  value: string | number | Date,
  timeZone?: string | null | undefined,
): string {
  if (typeof value === "string") {
    const clock = parseWallClock(value);
    // Already a wall clock: render it verbatim, never shifted into another zone.
    if (clock) {
      return new Intl.DateTimeFormat("en-IN", TIME_OPTIONS).format(
        new Date(2000, 0, 1, clock.hours, clock.minutes),
      );
    }
  }
  const date = toDate(value);
  if (!date) return "—";
  return dateTimeFormat(TIME_OPTIONS, timeZone).format(date);
}

/**
 * "Today", "Tomorrow", or a short date — used on booking cards.
 *
 * `now` is a parameter rather than a `Date.now()` read so callers driving a
 * ticking clock relabel their cards when the day rolls over.
 */
export function relativeDay(value: string | number | Date, now: Date = new Date()): string {
  const date = toDate(value);
  if (!date) return "—";
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(date) - startOf(now)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";
  return formatDate(date);
}

/* -------------------------------------------------------------------------- */
/* Booking date + time                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A booking's schedule is spread over four fields — `appointmentDate` (a
 * timestamp), `dateLocal` (the patient's calendar date), `timeZone`, and the
 * `startTime` / `endTime` wall clocks. Backends differ in which of them carry
 * the time of day, so every helper below resolves the day and the clock
 * separately and falls back rather than rendering a wrong or empty value.
 */
function bookingDay(booking: Booking): Date | null {
  return toDate(booking.dateLocal || booking.appointmentDate);
}

/**
 * Wall clocks are read in the viewer's own zone. The doctor and the booking
 * share a zone in practice, and this instant is only ever used for ordering and
 * for "has it happened yet" — never for display.
 */
function bookingInstant(booking: Booking, time: string): Date | null {
  const clock = typeof time === "string" ? parseWallClock(time) : null;
  if (!clock) {
    // Not a wall clock — either a full timestamp of its own, or missing.
    const own = time ? toDate(time) : null;
    return own ?? toDate(booking.appointmentDate);
  }
  const day = bookingDay(booking);
  if (!day) return null;
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), clock.hours, clock.minutes);
}

export function bookingStartsAt(booking: Booking): Date | null {
  return bookingInstant(booking, booking.startTime);
}

export function bookingEndsAt(booking: Booking): Date | null {
  return bookingInstant(booking, booking.endTime) ?? bookingStartsAt(booking);
}

/** "Today", "Tomorrow", 13 Aug 2026 — anchored on the booking's own date. */
export function formatBookingDay(booking: Booking, now: Date = new Date()): string {
  return relativeDay(booking.dateLocal || booking.appointmentDate, now);
}

/**
 * True when the timestamp actually carries a time of day. `appointmentDate`
 * arrives as midnight UTC — a calendar date in disguise — so midnight in UTC
 * counts as "no time" even when it is some other hour in the viewer's zone.
 */
function hasTimeOfDay(date: Date, timeZone?: string | null | undefined): boolean {
  if (date.getUTCHours() === 0 && date.getUTCMinutes() === 0) return false;
  const parts = dateTimeFormat({ hour: "2-digit", minute: "2-digit", hour12: false }, timeZone)
    .formatToParts(date)
    .reduce<Record<string, string>>((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
  return Number(parts["hour"]) !== 0 || Number(parts["minute"]) !== 0;
}

/** "9:00 am – 9:30 am". Never blank: says so plainly when the backend sent no time. */
export function formatBookingTimeRange(booking: Booking): string {
  const start = booking.startTime ? formatTime(booking.startTime, booking.timeZone) : "—";
  const end = booking.endTime ? formatTime(booking.endTime, booking.timeZone) : "—";

  if (start !== "—") return end === "—" || end === start ? start : `${start} – ${end}`;
  if (end !== "—") return end;

  // Last resort: the appointment timestamp, but only when it actually carries a
  // time — a date stored as midnight would otherwise read as a 12:00 am slot.
  const stamp = toDate(booking.appointmentDate);
  if (stamp && hasTimeOfDay(stamp, booking.timeZone)) {
    return formatTime(stamp, booking.timeZone);
  }
  return "Time not set";
}

/** "Today · 9:00 am – 9:30 am" — the one-line schedule shown on every card. */
export function formatBookingSchedule(booking: Booking, now: Date = new Date()): string {
  return `${formatBookingDay(booking, now)} · ${formatBookingTimeRange(booking)}`;
}

/**
 * Still ahead of us. Measured against the end of the slot, so a consultation
 * that is running right now still counts as upcoming — the old check used the
 * appointment timestamp alone, which dropped today's bookings out of the list
 * the moment midnight passed.
 */
export function isBookingUpcoming(booking: Booking, now: Date = new Date()): boolean {
  const end = bookingEndsAt(booking);
  return end ? end.getTime() >= now.getTime() : false;
}

/** The slot has started — used to tell "not yet" apart from "in progress". */
export function hasBookingStarted(booking: Booking, now: Date = new Date()): boolean {
  const start = bookingStartsAt(booking);
  return start ? start.getTime() <= now.getTime() : false;
}

/** Scheduled for today, whatever the time of day. */
export function isBookingToday(booking: Booking, now: Date = new Date()): boolean {
  const day = bookingDay(booking);
  if (!day) return false;
  return (
    day.getFullYear() === now.getFullYear() &&
    day.getMonth() === now.getMonth() &&
    day.getDate() === now.getDate()
  );
}

/** Sort key; bookings with no resolvable date sort last. */
export function bookingSortKey(booking: Booking): number {
  return bookingStartsAt(booking)?.getTime() ?? 0;
}
